/**
 * TypeScript timeline for UI display purposes
 * Tracks timeline position based on global clock for visualization
 */

import { SAMPLE_RATE } from './bytecode.ts'
import { parseTimelineString, type TimelineSegment } from './timeline-parser.ts'

export interface TimelinePosition {
  segmentIndex: number
  positionInSegment: number // 0-1, how far through the current segment
  currentSegment: TimelineSegment
  currentValue: number // 0-1, the current timeline value
}

export class UITimeline {
  private segments: TimelineSegment[] = []
  private totalDivisions: number = 0
  private beat: number = 0.25 // Default 1/4
  private oversamplingFactor: number = 1
  private sampleRate: number = SAMPLE_RATE

  constructor(
    pattern: string,
    beat: number = 0.25,
    oversamplingFactor: number = 1,
    sampleRate: number = SAMPLE_RATE,
  ) {
    this.oversamplingFactor = oversamplingFactor
    this.sampleRate = sampleRate
    this.updatePattern(pattern, beat)
  }

  /**
   * Update timeline pattern while preserving runtime state
   */
  updatePattern(pattern: string, beat: number = 0.25): void {
    try {
      this.segments = parseTimelineString(pattern)
      this.beat = beat
      this.totalDivisions = this.segments.reduce((sum, seg) => sum + seg.divisions, 0)
    }
    catch (e) {
      console.error('[UITimeline] Failed to parse pattern:', e)
      this.segments = []
      this.totalDivisions = 0
    }
  }

  /**
   * Get current position in timeline for visualization
   */
  getPosition(
    globalSampleCounter: number,
    bpm: number = 120,
  ): TimelinePosition | null {
    if (this.segments.length === 0 || this.totalDivisions <= 0) {
      return null
    }

    const samplesPerQuarterBeat = (60.0 / (bpm * this.oversamplingFactor)) * this.sampleRate * 4.0
    const samplesPerDivision = samplesPerQuarterBeat * this.beat
    const samplesPerCycle = samplesPerDivision * this.totalDivisions

    // Calculate position in cycle (wrap at samplesPerCycle)
    let cycleSample = globalSampleCounter % samplesPerCycle
    if (cycleSample < 0) {
      cycleSample += samplesPerCycle
    }
    // Ensure we're strictly within cycle bounds
    if (cycleSample >= samplesPerCycle) {
      cycleSample = 0
    }

    // Find which segment we're in and position within segment
    let currentDivisions = 0
    let segmentIndex = -1
    let segmentStartDivisions = 0
    let currentSegment: TimelineSegment | null = null

    for (let j = 0; j < this.segments.length; j++) {
      const seg = this.segments[j]
      const segmentEndDivisions = segmentStartDivisions + seg.divisions
      const segmentStartSamples = segmentStartDivisions * samplesPerDivision
      const segmentEndSamples = segmentEndDivisions * samplesPerDivision

      // Check if we're in this segment
      if (cycleSample >= segmentStartSamples && cycleSample < segmentEndSamples) {
        segmentIndex = j
        currentSegment = seg
        currentDivisions = segmentStartDivisions
        break
      }

      segmentStartDivisions = segmentEndDivisions
    }

    // If we didn't find a segment, use first or last segment as fallback
    if (segmentIndex < 0) {
      if (cycleSample < samplesPerDivision * 0.01) {
        segmentIndex = 0
        currentSegment = this.segments[0]
        currentDivisions = 0
      }
      else {
        segmentIndex = this.segments.length - 1
        currentSegment = this.segments[this.segments.length - 1]
        currentDivisions = this.totalDivisions - currentSegment.divisions
      }
    }

    if (!currentSegment) {
      return null
    }

    // Calculate position within segment (0-1)
    const segmentStartSamples = currentDivisions * samplesPerDivision
    const segmentEndSamples = (currentDivisions + currentSegment.divisions) * samplesPerDivision
    const segmentLengthSamples = segmentEndSamples - segmentStartSamples
    const positionInSegment = segmentLengthSamples > 0
      ? (cycleSample - segmentStartSamples) / segmentLengthSamples
      : 0

    // Calculate current value based on segment type
    let currentValue = 0
    if (currentSegment.type === 'rise') {
      // Rise: linear interpolation from 0 to 1
      currentValue = Math.max(0, Math.min(1, positionInSegment))
    }
    else if (currentSegment.type === 'fall') {
      // Fall: linear interpolation from 1 to 0
      currentValue = Math.max(0, Math.min(1, 1 - positionInSegment))
    }
    else if (currentSegment.type === 'hold') {
      // Hold: constant value
      currentValue = currentSegment.value
    }

    return {
      segmentIndex,
      positionInSegment: Math.max(0, Math.min(1, positionInSegment)),
      currentSegment,
      currentValue,
    }
  }

  /**
   * Get all segment boundaries in samples (for rendering timeline)
   */
  getSegmentBoundaries(
    bpm: number = 120,
  ): Array<{ segmentIndex: number; samplePosition: number; segment: TimelineSegment }> {
    if (this.segments.length === 0 || this.totalDivisions <= 0) {
      return []
    }

    const samplesPerQuarterBeat = (60.0 / (bpm * this.oversamplingFactor)) * this.sampleRate * 4.0
    const samplesPerDivision = samplesPerQuarterBeat * this.beat
    const samplesPerCycle = samplesPerDivision * this.totalDivisions

    const boundaries: Array<{ segmentIndex: number; samplePosition: number; segment: TimelineSegment }> = []
    let currentDivisions = 0

    for (let i = 0; i < this.segments.length; i++) {
      const seg = this.segments[i]
      const segmentStartSamples = currentDivisions * samplesPerDivision
      boundaries.push({
        segmentIndex: i,
        samplePosition: segmentStartSamples,
        segment: seg,
      })
      currentDivisions += seg.divisions
    }

    // Add end of cycle
    boundaries.push({
      segmentIndex: this.segments.length,
      samplePosition: samplesPerCycle,
      segment: this.segments[0], // Cycle repeats
    })

    return boundaries
  }

  /**
   * Get total cycle length in samples
   */
  getCycleLength(bpm: number = 120): number {
    if (this.totalDivisions <= 0) {
      return 0
    }

    const samplesPerQuarterBeat = (60.0 / (bpm * this.oversamplingFactor)) * this.sampleRate * 4.0
    const samplesPerDivision = samplesPerQuarterBeat * this.beat
    return samplesPerDivision * this.totalDivisions
  }

  /**
   * Get segments
   */
  getSegments(): TimelineSegment[] {
    return [...this.segments]
  }
}

export interface TimelineConfig {
  id: number
  pattern: string
  beat: number
}

/**
 * Manager for batch updates of multiple timelines
 * Tracks state between updates for visualization
 */
export class TimelineManager {
  private timelines = new Map<number, UITimeline>()
  private configs = new Map<number, TimelineConfig>()
  private lastSampleCounter: number = -1
  private oversamplingFactor: number = 1
  private sampleRate: number = SAMPLE_RATE

  constructor(oversamplingFactor: number = 1, sampleRate: number = SAMPLE_RATE) {
    this.oversamplingFactor = oversamplingFactor
    this.sampleRate = sampleRate
  }

  /**
   * Set timelines to track
   */
  setTimelines(
    timelines: Map<number, TimelineConfig> | TimelineConfig[],
  ): void {
    const timelineMap = Array.isArray(timelines)
      ? new Map(timelines.map(t => [t.id, t]))
      : timelines

    // Update existing timelines or create new ones
    for (const [id, config] of timelineMap) {
      const existingTimeline = this.timelines.get(id)
      if (existingTimeline) {
        // Update existing timeline with new pattern and beat
        existingTimeline.updatePattern(config.pattern, config.beat)
        const existingConfig = this.configs.get(id)
        if (existingConfig) {
          existingConfig.pattern = config.pattern
          existingConfig.beat = config.beat
        }
      }
      else {
        // Create new timeline
        const timeline = new UITimeline(
          config.pattern,
          config.beat,
          this.oversamplingFactor,
          this.sampleRate,
        )
        this.timelines.set(id, timeline)
        this.configs.set(id, config)
      }
    }

    // Remove timelines that are no longer in the config
    for (const [id] of this.timelines) {
      if (!timelineMap.has(id)) {
        this.timelines.delete(id)
        this.configs.delete(id)
      }
    }
  }

  /**
   * Update timeline pattern
   */
  updateTimeline(id: number, pattern: string, beat: number = 0.25): void {
    const timeline = this.timelines.get(id)
    if (timeline) {
      timeline.updatePattern(pattern, beat)
      const config = this.configs.get(id)
      if (config) {
        config.pattern = pattern
        config.beat = beat
      }
    }
  }

  /**
   * Get current position for a specific timeline
   */
  getPosition(timelineId: number, globalSampleCounter: number, bpm: number = 120): TimelinePosition | null {
    const timeline = this.timelines.get(timelineId)
    if (!timeline) {
      return null
    }

    return timeline.getPosition(globalSampleCounter, bpm)
  }

  /**
   * Get current positions for all timelines
   */
  getAllPositions(globalSampleCounter: number, bpm: number = 120): Map<number, TimelinePosition> {
    const positions = new Map<number, TimelinePosition>()
    for (const [id, timeline] of this.timelines) {
      const position = timeline.getPosition(globalSampleCounter, bpm)
      if (position) {
        positions.set(id, position)
      }
    }
    return positions
  }

  /**
   * Get segment boundaries for a timeline (for rendering)
   */
  getSegmentBoundaries(timelineId: number,
    bpm: number = 120): Array<{ segmentIndex: number; samplePosition: number; segment: TimelineSegment }>
  {
    const timeline = this.timelines.get(timelineId)
    if (!timeline) {
      return []
    }

    return timeline.getSegmentBoundaries(bpm)
  }

  /**
   * Reset tracking state
   */
  reset(): void {
    this.lastSampleCounter = -1
  }

  /**
   * Initialize counter
   */
  initializeCounter(globalSampleCounter: number): void {
    this.lastSampleCounter = globalSampleCounter
  }
}
